import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { auth } from '../firebase/config';
import { 
  signInWithEmailAndPassword, 
  signOut,
  onAuthStateChanged,
  User,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { isUserAuthorized, updateUserLastLogin, getUserRole } from '../services/userManagementService';

// Define a custom user type that can be either Firebase User or our dev user
interface DevUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

type AppUser = User | DevUser;

interface AuthContextType {
  isAuthenticated: boolean;
  currentUser: AppUser | null;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithGoogle: () => Promise<boolean>;
  logout: () => Promise<void>;
  isLoading: boolean;
  userRole: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    // Check for development mode auth
    const devToken = localStorage.getItem('dev_auth_token');
    const devUser = localStorage.getItem('dev_user');
    
    if (devToken && devUser) {
      try {
        const parsedUser = JSON.parse(devUser) as DevUser;
        setCurrentUser(parsedUser);
        setUserRole(parsedUser.role);
        setIsLoading(false);
        return () => {}; // No cleanup needed for dev mode
      } catch (error) {
        console.error('Error parsing dev user:', error);
        // Continue with normal auth if dev user parsing fails
      }
    }
    
    // Normal Firebase auth subscription
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && user.email) {
        // Check if the user is authorized
        const authorized = await isUserAuthorized(user.email);
        
        if (authorized) {
          setCurrentUser(user);
          // Get user role
          const role = await getUserRole(user.email);
          setUserRole(role);
          
          // Update last login time
          await updateUserLastLogin(user.email);
        } else {
          // Not authorized, sign them out
          await signOut(auth);
          setCurrentUser(null);
          setUserRole(null);
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      
      setIsLoading(false);
    });

    // Unsubscribe when the component unmounts
    return unsubscribe;
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      // Check if the user is authorized before attempting to sign in
      const authorized = await isUserAuthorized(email);
      
      if (!authorized) {
        console.error("This email is not authorized to access the admin panel");
        return false;
      }
      
      await signInWithEmailAndPassword(auth, email, password);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if the Google user is authorized
      if (result.user && result.user.email) {
        const authorized = await isUserAuthorized(result.user.email);
        
        if (!authorized) {
          console.error("This Google account is not authorized to access the admin panel");
          await signOut(auth);
          return false;
        }
        
        // Update last login time
        await updateUserLastLogin(result.user.email);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Google login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Clear dev mode auth if present
      localStorage.removeItem('dev_auth_token');
      localStorage.removeItem('dev_user');
      
      // Also sign out from Firebase if authenticated
      await signOut(auth);
      setUserRole(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    currentUser,
    isAuthenticated: !!currentUser,
    login,
    loginWithGoogle,
    logout,
    isLoading,
    userRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 